import classNames from 'classnames'
import React from 'react'

import Loading from '@shared-components/ui/Loading'

export interface ButtonProps {
  children?: React.ReactNode
  className?: string
  disabled?: boolean
  loading?: boolean
  onButtonClick?: (e?: React.SyntheticEvent) => void
  shape?: 'rounded' | 'square'
  size?: 'small' | 'medium' | 'large' | 'navbar' | 'badge'
  type?: 'button' | 'reset' | 'submit'
  variant?:
    | 'primary'
    | 'dark-green'
    | 'green'
    | 'light-green'
    | 'blue'
    | 'light-blue'
    | 'earth'
    | 'light-earth'
    | 'white'
    | 'gray'
    | 'transparent'
}

/**
 * Utility method to get the classes that match our brand style.
 * @param variant One of the variant strings defined in the interface.
 * @param disabled Whether the button is disabled.
 * @returns a set of classes that match the variant.
 */
function getVariance(variant: ButtonProps['variant'], disabled: ButtonProps['disabled']) {
  const defaultDisabledClasses = 'bg-gray-600 text-new-white'
  const whiteDisabledClasses = 'border-2 border-gray-500 text-gray-500'
  switch (variant) {
    case 'primary':
      return disabled
        ? defaultDisabledClasses
        : 'text-new-white active:text-new-white hover:text-black bg-black hover:bg-gray-200 active:bg-gray-200'
    case 'dark-green':
      return disabled
        ? defaultDisabledClasses
        : 'text-new-green-100 active:text-new-green-100 bg-new-green-800 hover:bg-new-green-700 active:bg-new-green-600'
    case 'green':
      return disabled
        ? defaultDisabledClasses
        : 'text-new-white active:text-new-white bg-new-green-600 hover:bg-new-green-700 active:bg-new-green-800'
    case 'light-green':
      return disabled
        ? defaultDisabledClasses
        : 'text-new-green-600 active:text-new-green-300 bg-new-green-100 hover:bg-new-green-200 active:bg-new-green-800'
    case 'blue':
      return disabled
        ? defaultDisabledClasses
        : 'text-new-white active:text-new-white bg-new-blue-500 hover:bg-new-blue-600 active:bg-new-blue-700'
    case 'light-blue':
      return disabled
        ? defaultDisabledClasses
        : 'text-new-blue-700 bg-new-blue-100 hover:bg-new-blue-200 active:bg-new-blue-250 active:text-new-blue-800'
    case 'earth':
      return disabled
        ? defaultDisabledClasses
        : 'text-taupe-300 active:text-taupe-300 bg-earth hover:bg-new-brown-600 active:bg-new-brown-500'
    case 'light-earth':
      return disabled ? defaultDisabledClasses : 'text-earth bg-taupe-100 hover:bg-taupe-200 active:bg-taupe-300'
    case 'gray':
      return disabled
        ? whiteDisabledClasses
        : 'text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 active:text-gray-800'
    case 'white':
      return `${
        disabled
          ? whiteDisabledClasses
          : 'text-gray-800 border-2 border-gray-800 hover:bg-gray-100 active:bg-gray-200 hover:text-gray-800'
      } bg-new-white`
    case 'transparent':
      return `${
        disabled
          ? whiteDisabledClasses
          : 'text-new-white border-2 border-new-white hover:bg-gray-100 active:bg-gray-200 hover:text-gray-800'
      } bg-new-white/20`
    default:
      return `${
        disabled
          ? whiteDisabledClasses
          : 'text-gray-800 border-2 border-gray-800 hover:bg-gray-100 active:bg-gray-200 hover:text-gray-800'
      }`
  }
}

/**
 * Utility method to return the correct class based on props.
 * @param size One of the sizes defined in the interface.
 * @returns a set of classes that match the size.
 */
function getSize(size: ButtonProps['size']): string {
  switch (size) {
    case 'navbar':
      return 'text-lg py-2 px-3'
    case 'large':
      return 'font-medium text-md tracking-[0.06375rem] font-bold py-6 px-6'
    case 'medium':
      return 'text-xl py-3 px-6'
    case 'small':
      return 'text-small p-2'
    case 'badge':
      return 'text-smx py-1 px-1'

    default:
      return 'min-h-sm min-w-[21.75rem] font-medium text-md tracking-[0.06375rem] py-2 px-6'
  }
}

export const LoadingButton: React.FC = () => (
  <>
    <Loading className="inline-block w-5 h-4 mr-2" />
    Just a moment
  </>
)

export function getBaseButtonClassNames(
  disabled: ButtonProps['disabled'],
  shape: ButtonProps['shape'],
  size: ButtonProps['size'],
  variant: ButtonProps['variant'],
): string {
  return classNames(
    'select-none transition-all appearance-none dd-privacy-allow outline-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-new-blue-600 uppercase',
    getVariance(variant, disabled),
    getSize(size),
    shape === 'square' ? 'rounded-xs' : 'rounded-full',
  )
}

/**
 * A button component that adheres to our brand style.
 * @param props The props for the button.
 * @param props.className Classes to adjust the button's styling. Existing styles cannot be over-ridden.
 * @param props.children Elements to render inside of the button outline.
 * @param props.disabled Whether the button is disabled.
 * @param props.loading Whether the button is loading.
 * @param props.onButtonClick Callback the button will call when clicked.
 * @param props.shape Whether the button is rounded or square.
 * @param props.size Large or small button size.
 * @param props.type The Button's type (button, submit).
 * @param props.variant Defined button variants that match our style guide.
 * @returns a Button component.
 */
export const Button: React.FC<ButtonProps> = ({
  className,
  children,
  onButtonClick,
  shape = 'rounded',
  size,
  type = 'button',
  variant = 'white',
  disabled = false,
  loading = false,
}: ButtonProps) => (
  <button
    className={classNames(
      loading && 'flex justify-between items-center',
      disabled && 'cursor-not-allowed',
      'leading-none',
      getBaseButtonClassNames(disabled, shape, size, variant),
      !size && 'inline-block',
      className || 'w-full',
    )}
    disabled={disabled || loading}
    onClick={onButtonClick}
    type={type}
  >
    {loading && (
      <div className="flex items-center justify-center w-full">
        <LoadingButton />
      </div>
    )}
    {!loading && children}
  </button>
)

export default Button
